import { formatLocalizedDate } from '@/lib/utils/date-locale'
import { Plan } from '@/types/generated/public_api'

/**
 * Format a Stripe amount for display. `unitAmount` is the smallest currency unit
 * as a string (the proto carries int64 as a string). The number of minor units
 * per major unit is currency-dependent — 100 for USD, 1 for zero-decimal
 * currencies like JPY/KRW, 1000 for BHD — so derive the divisor from the
 * currency's own fraction digits rather than assuming cents. Localized to the
 * app locale so prices match every other number in the app. Whole amounts render
 * without decimals ($20), fractional ones with the currency's digits ($19.99).
 */
export function formatStripeAmount(
  unitAmount: string,
  currency: string,
  locale: string | undefined
): string {
  const minorUnits = Number(unitAmount)
  const safeMinor = Number.isFinite(minorUnits) ? minorUnits : 0
  const code = (currency || 'usd').toUpperCase()
  try {
    const fractionDigits =
      new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: code,
      }).resolvedOptions().maximumFractionDigits ?? 2
    const amount = safeMinor / 10 ** fractionDigits
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      minimumFractionDigits: Number.isInteger(amount) ? 0 : fractionDigits,
    }).format(amount)
  } catch {
    // Unknown ISO code — fall back to a bare number plus the raw code.
    return `${safeMinor / 100} ${code}`
  }
}

/**
 * Unix-seconds timestamp (proto int64-as-string) → date localized to the app
 * locale (so it matches every other date in the app), or null when unset.
 */
export function formatPeriodEnd(
  currentPeriodEnd: string,
  locale: string | undefined
): string | null {
  const seconds = Number(currentPeriodEnd)
  if (!Number.isFinite(seconds) || seconds <= 0) return null
  return formatLocalizedDate(new Date(seconds * 1000), locale, 'expiryFull')
}

/** Whole days remaining until a unix-seconds timestamp (never negative). */
export function daysUntil(currentPeriodEnd: string): number {
  const seconds = Number(currentPeriodEnd)
  if (!Number.isFinite(seconds) || seconds <= 0) return 0
  return Math.max(0, Math.ceil((seconds * 1000 - Date.now()) / 86_400_000))
}

/** A plan's price for one interval normalized to a per-month amount (so an
 * interval_count other than 1 still compares cleanly), or null when absent/free. */
function perMonthAmount(plan: Plan, interval: 'month' | 'year'): number | null {
  const price = plan.prices.find((p) => p.interval === interval)
  if (!price) return null
  const amount = Number(price.unitAmount)
  if (!Number.isFinite(amount) || amount <= 0) return null
  const count = price.intervalCount || 1
  return amount / (interval === 'year' ? 12 * count : count)
}

/**
 * Largest monthly→yearly discount across the catalog as a whole percent, for the
 * "Save X%" hint on the yearly toggle — or null when no plan prices both a paid
 * monthly and yearly interval. Derived from live Stripe amounts so it can't drift
 * from the prices shown on the cards.
 */
export function maxYearlySavingsPercent(plans: Plan[]): number | null {
  let best: number | null = null
  for (const plan of plans) {
    const monthly = perMonthAmount(plan, 'month')
    const yearly = perMonthAmount(plan, 'year')
    if (monthly === null || yearly === null || yearly >= monthly) continue
    const pct = Math.round(((monthly - yearly) / monthly) * 100)
    if (pct > 0 && (best === null || pct > best)) best = pct
  }
  return best
}
